import { Controller } from "@hotwired/stimulus"

// "YYYY-MM-DD" de una fecha local. No usar toISOString(): da el día en UTC, y
// desde las 21:00 en Argentina el PDF salía con la fecha de mañana.
function formatLocalDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export default class extends Controller {
  static targets = ["textarea", "modal", "modalTextarea", "modalTitle", "modalDay", "modalMealtime", "patientWeight", "startDate", "endDate", "observations", "mealtimeHour"]
  static values = {
    autoSaveDelay: { type: Number, default: 1000 },
    menuId: { type: Number, default: 0 },
    autoSaveUrl: { type: String, default: '' },
    nutritionist: { type: String, default: '' },
    logoUrl: { type: String, default: '' },
    salmonUrl: { type: String, default: '' },
    forkUrl: { type: String, default: '' },
    grassUrl: { type: String, default: '' },
    carrotUrl: { type: String, default: '' },
    patients: { type: Array, default: [] },
    days: { type: Array, default: [] },
    dayLabels: { type: Object, default: {} },
    mealtimes: { type: Array, default: [] },
    mealtimeLabels: { type: Object, default: {} },
    mealtimeColors: { type: Object, default: {} }
  }

  connect() {
    this.saveTimeout = null
    this.currentTextarea = null
    this.isAutoSaving = false
    this.clipboardContent = null
    this.clipboardLabel = null
    this.setupModal()
    this.setupPreviewModal()

    // Start 2-minute auto-save timer
    if (this.autoSaveUrlValue) {
      this.autoSaveTimer = setTimeout(() => this.performAutoSave(), 120000)
    }

    // Listen for custom download-pdf event from external buttons
    this.handleDownloadPdf = (e) => {
      const btn = e.detail?.button
      if (btn) {
        this.downloadPdfWithButton(btn)
      }
    }
    this.element.addEventListener('download-pdf', this.handleDownloadPdf)
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer)
    }
    if (this.handleDownloadPdf) {
      this.element.removeEventListener('download-pdf', this.handleDownloadPdf)
    }
  }

  async downloadPdfWithButton(btn) {
    const originalHtml = btn.innerHTML
    btn.disabled = true
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Generando...'

    try {
      await this.generateAndDownloadPdf()
    } finally {
      btn.disabled = false
      btn.innerHTML = originalHtml
    }
  }

  async generateAndDownloadPdf() {
    // Generar el HTML del preview
    const previewHtml = this.generatePreviewFromForm()

    // Crear contenedor temporal
    const tempContainer = document.createElement('div')
    tempContainer.style.position = 'absolute'
    tempContainer.style.left = '-9999px'
    tempContainer.style.top = '0'
    tempContainer.style.width = '1200px'
    tempContainer.style.background = 'white'
    tempContainer.innerHTML = previewHtml
    document.body.appendChild(tempContainer)


    // Load html2pdf library if not already loaded
    if (!window.html2pdf) {
      await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js')
    }

    // Wait for content and images to load
    await new Promise(resolve => setTimeout(resolve, 500))

    // Convert filtered images to white
    await this.convertFilteredImagesToWhite(tempContainer)

    // Get filename
    const patientSelect = this.element.querySelector('[name="custom_menu[patient_id]"]')
    const patientId = patientSelect?.value
    let fileName = 'menu-personalizado'

    if (patientId) {
      const patients = this.patientsValue
      const patient = patients.find(p => p.id == patientId)
      if (patient?.name) {
        fileName = `menu-${patient.name.toLowerCase().replace(/\s+/g, '-')}`
      }
    }

    const today = formatLocalDate(new Date())
    fileName = `${fileName}-${today}.pdf`

    const contentHeight = tempContainer.scrollHeight
    const contentWidth = tempContainer.scrollWidth

    const opt = {
      margin: 0,
      filename: fileName,
      image: { type: 'png', quality: 1 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        scrollX: 0,
        scrollY: 0,
        height: contentHeight,
        windowHeight: contentHeight
      },
      jsPDF: {
        unit: 'px',
        format: [contentWidth, contentHeight],
        orientation: 'landscape',
        hotfixes: ['px_scaling']
      }
    }

    try {
      await html2pdf().set(opt).from(tempContainer).save()
    } finally {
      // Remove temp container
      document.body.removeChild(tempContainer)
    }
  }

  setupModal() {
    if (!document.getElementById('menuCellEditorModal')) {
      const modalHtml = `
        <div class="modal fade" id="menuCellEditorModal" tabindex="-1" role="dialog" data-custom-menu-editor-target="modal">
          <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
              <div class="modal-header" style="background: linear-gradient(135deg, #33A036 0%, #28a745 100%);">
                <h5 class="modal-title text-white">
                  <i class="fas fa-edit mr-2"></i>
                  <span data-custom-menu-editor-target="modalTitle">Editar Menú</span>
                </h5>
                <button type="button" class="close text-white" data-dismiss="modal">
                  <span>&times;</span>
                </button>
              </div>
              <div class="modal-body p-0">
                <div class="border-bottom bg-light p-2 d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
                  <div class="btn-group btn-group-sm mr-2">
                    <button type="button" class="btn btn-outline-secondary" data-action="click->custom-menu-editor#formatBold" title="Negrita">
                      <i class="fas fa-bold"></i>
                    </button>
                    <button type="button" class="btn btn-outline-secondary" data-action="click->custom-menu-editor#formatItalic" title="Cursiva">
                      <i class="fas fa-italic"></i>
                    </button>
                  </div>
                  <div class="btn-group btn-group-sm mr-2">
                    <button type="button" class="btn btn-outline-secondary" data-action="click->custom-menu-editor#formatBullet" title="Viñeta">
                      <i class="fas fa-list-ul"></i>
                    </button>
                    <button type="button" class="btn btn-outline-secondary" data-action="click->custom-menu-editor#formatNumber" title="Lista numerada">
                      <i class="fas fa-list-ol"></i>
                    </button>
                  </div>
                  <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary" data-action="click->custom-menu-editor#insertArrow" title="Flecha">
                      →
                    </button>
                    <button type="button" class="btn btn-outline-secondary" data-action="click->custom-menu-editor#insertCheck" title="Check">
                      ✓
                    </button>
                  </div>
                  <div class="btn-group btn-group-sm mr-2">
                    <button type="button" class="btn btn-outline-info" id="modalCopyBtn" data-action="click->custom-menu-editor#copyFromModal" title="Copiar contenido de esta celda">
                      <i class="fas fa-copy"></i>
                    </button>
                    <button type="button" class="btn btn-outline-info" id="modalPasteBtn" data-action="click->custom-menu-editor#pasteToModal" title="Pegar contenido copiado">
                      <i class="fas fa-paste"></i>
                    </button>
                  </div>
                  <span id="modalClipboardIndicator" class="badge badge-info px-2 py-1 mr-2" style="display: none; font-size: 0.7rem;">
                    <i class="fas fa-clipboard mr-1"></i><span class="clipboard-label"></span>
                  </span>
                  <span class="text-muted small ml-auto">
                    <i class="fas fa-info-circle mr-1"></i>
                    Usa **texto** para negrita, *texto* para cursiva
                  </span>
                </div>
                <div class="p-3">
                  <textarea
                    class="form-control"
                    data-custom-menu-editor-target="modalTextarea"
                    rows="12"
                    style="font-size: 1rem; line-height: 1.6; resize: vertical;"
                    placeholder="Escribe el contenido del menú...

Ejemplos de formato:
• Usar viñetas para listar opciones
**Negrita** para destacar
1. Listas numeradas para pasos"></textarea>
                </div>
                <div class="border-top bg-light p-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <small class="text-muted font-weight-bold">
                      <i class="fas fa-eye mr-1"></i> Vista previa
                    </small>
                    <small class="text-muted" data-custom-menu-editor-target="charCount">0 caracteres</small>
                  </div>
                  <div class="preview-content p-2 bg-white rounded border"
                       style="min-height: 60px; font-size: 0.9rem;"
                       data-custom-menu-editor-target="preview"></div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">
                  <i class="fas fa-times mr-1"></i> Cancelar
                </button>
                <button type="button" class="btn btn-success" id="menuCellEditorApplyBtn">
                  <i class="fas fa-check mr-1"></i> Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      `
      document.body.insertAdjacentHTML('beforeend', modalHtml)

      const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
      if (modalTextarea) {
        modalTextarea.addEventListener('input', () => this.updatePreview())
      }

      // Bind apply button click event
      const applyBtn = document.getElementById('menuCellEditorApplyBtn')
      if (applyBtn) {
        applyBtn.addEventListener('click', () => this.saveAndClose())
      }
    }
  }

  setupPreviewModal() {
    if (!document.getElementById('menuPreviewModal')) {
      const previewModalHtml = `
        <div class="modal fade" id="menuPreviewModal" tabindex="-1" role="dialog">
          <div class="modal-dialog modal-xl" role="document" style="max-width: 95%; margin: 20px auto;">
            <div class="modal-content" style="border: none; border-radius: 12px; overflow: hidden;">
              <div class="modal-body p-0" id="menuPreviewContent" style="max-height: 85vh; overflow-y: auto;">
                <!-- Content loaded dynamically -->
              </div>
              <div class="modal-footer" style="background: #f8f9fa; border-top: 1px solid #e9ecef; padding: 12px 25px;">
                <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">
                  <i class="fas fa-times mr-1"></i> Cerrar
                </button>
                <button type="button" class="btn btn-success" id="menuPreviewPrintBtn" style="background: linear-gradient(135deg, #33A036 0%, #28a745 100%); border: none;">
                  <i class="fas fa-file-pdf mr-1"></i> Descargar PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      `
      document.body.insertAdjacentHTML('beforeend', previewModalHtml)

      // Bind print button
      const printBtn = document.getElementById('menuPreviewPrintBtn')
      if (printBtn) {
        printBtn.addEventListener('click', () => this.printPreview())
      }
    }
  }

  async printPreview() {
    const content = document.getElementById('menuPreviewContent')
    if (!content) return

    const printBtn = document.getElementById('menuPreviewPrintBtn')
    if (printBtn) {
      printBtn.disabled = true
      printBtn.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Generando PDF...'
    }

    // Load html2pdf library if not already loaded
    if (!window.html2pdf) {
      await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js')
    }

    // Temporarily remove scroll restrictions to capture full content
    const originalStyle = content.style.cssText
    content.style.maxHeight = 'none'
    content.style.overflow = 'visible'
    content.style.height = 'auto'

    // Wait for reflow
    await new Promise(resolve => setTimeout(resolve, 100))

    // Convert filtered images to white before PDF generation
    await this.convertFilteredImagesToWhite(content)

    // Get patient name for filename
    const patientSelect = this.element.querySelector('[name="custom_menu[patient_id]"]')
    const patientId = patientSelect?.value
    let fileName = 'menu-personalizado'

    if (patientId) {
      const patients = this.patientsValue
      const patient = patients.find(p => p.id == patientId)
      if (patient?.name) {
        fileName = `menu-${patient.name.toLowerCase().replace(/\s+/g, '-')}`
      }
    }

    const today = formatLocalDate(new Date())
    fileName = `${fileName}-${today}.pdf`

    // Get actual content dimensions after removing restrictions
    const contentHeight = content.scrollHeight
    const contentWidth = content.scrollWidth

    const opt = {
      margin: 0,
      filename: fileName,
      image: { type: 'png', quality: 1 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        scrollX: 0,
        scrollY: 0,
        height: contentHeight,
        windowHeight: contentHeight
      },
      jsPDF: {
        unit: 'px',
        format: [contentWidth, contentHeight],
        orientation: 'landscape',
        hotfixes: ['px_scaling']
      }
    }

    try {
      await html2pdf().set(opt).from(content).save()
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Error al generar el PDF. Por favor intente nuevamente.')
    } finally {
      // Restore original styles
      content.style.cssText = originalStyle

      if (printBtn) {
        printBtn.disabled = false
        printBtn.innerHTML = '<i class="fas fa-file-pdf mr-1"></i> Descargar PDF'
      }
    }
  }

  async convertFilteredImagesToWhite(container) {
    const images = container.querySelectorAll('img[style*="filter"]')

    for (const img of images) {
      if (img.style.filter && img.style.filter.includes('invert')) {
        try {
          const whiteDataUrl = await this.createWhiteImage(img.src)
          img.src = whiteDataUrl
          img.style.filter = 'none'
        } catch (e) {
          console.warn('Could not convert image to white:', e)
        }
      }
    }
  }

  createWhiteImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')

        // Draw original image
        ctx.drawImage(img, 0, 0)

        // Get image data and invert colors to white
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data

        for (let i = 0; i < data.length; i += 4) {
          // If pixel is not transparent
          if (data[i + 3] > 0) {
            // Make it white
            data[i] = 255     // R
            data[i + 1] = 255 // G
            data[i + 2] = 255 // B
          }
        }

        ctx.putImageData(imageData, 0, 0)
        resolve(canvas.toDataURL('image/png'))
      }
      img.onerror = reject
      img.src = src
    })
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = src
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  // Descargar PDF directamente sin abrir preview
  async downloadPdf(event) {
    event.preventDefault()

    const btn = event.currentTarget
    const originalHtml = btn.innerHTML
    btn.disabled = true
    btn.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i> Generando...'

    try {
      // Generar el HTML del preview
      const previewHtml = this.generatePreviewFromForm()

      // Crear contenedor temporal
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '0'
      tempContainer.style.width = '1200px'
      tempContainer.innerHTML = previewHtml
      document.body.appendChild(tempContainer)

      // Load html2pdf library if not already loaded
      if (!window.html2pdf) {
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js')
      }

      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 200))

      // Convert filtered images to white
      await this.convertFilteredImagesToWhite(tempContainer)

      // Get filename
      const patientSelect = this.element.querySelector('[name="custom_menu[patient_id]"]')
      const patientId = patientSelect?.value
      let fileName = 'menu-personalizado'

      if (patientId) {
        const patients = this.patientsValue
        const patient = patients.find(p => p.id == patientId)
        if (patient?.name) {
          fileName = `menu-${patient.name.toLowerCase().replace(/\s+/g, '-')}`
        }
      }

      const today = formatLocalDate(new Date())
      fileName = `${fileName}-${today}.pdf`

      const contentHeight = tempContainer.scrollHeight
      const contentWidth = tempContainer.scrollWidth

      const opt = {
        margin: 0,
        filename: fileName,
        image: { type: 'png', quality: 1 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          scrollX: 0,
          scrollY: 0,
          height: contentHeight,
          windowHeight: contentHeight
        },
        jsPDF: {
          unit: 'px',
          format: [contentWidth, contentHeight],
          orientation: 'landscape',
          hotfixes: ['px_scaling']
        }
      }

      await html2pdf().set(opt).from(tempContainer).save()

      // Remove temp container
      document.body.removeChild(tempContainer)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Error al generar el PDF. Por favor intente nuevamente.')
    } finally {
      btn.disabled = false
      btn.innerHTML = originalHtml
    }
  }

  // Genera el HTML del preview desde los datos del formulario
  generatePreviewFromForm() {
    const menuName = this.element.querySelector('[name="custom_menu[name]"]')?.value || 'Menú Personalizado'
    const patientSelect = this.element.querySelector('[name="custom_menu[patient_id]"]')
    const patientId = patientSelect?.value
    let patientName = ''

    if (patientId) {
      const patients = this.patientsValue
      const patient = patients.find(p => p.id == patientId)
      patientName = patient?.name || ''
    }

    const today = new Date()
    const dateStr = today.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })

    const patientWeight = this.element.querySelector('[name="custom_menu[patient_weight]"]')?.value || ''
    const startDate = this.element.querySelector('[name="custom_menu[start_date]"]')?.value || ''
    const endDate = this.element.querySelector('[name="custom_menu[end_date]"]')?.value || ''

    const observationsField = this.element.querySelector('#custom_menu_observations') ||
                              this.element.querySelector('textarea[name="custom_menu[observations]"]') ||
                              this.element.querySelector('[name="custom_menu[observations]"]')
    const observations = observationsField?.value?.trim() || ''

    const mealtimeHours = this.getMealtimeHoursFromForm()
    const menuData = this.getMenuDataFromForm()

    return this.generatePreviewHtml(menuName, patientName, dateStr, menuData, {
      patientWeight,
      startDate,
      endDate,
      observations,
      mealtimeHours
    })
  }

  // Abrir vista previa profesional
  openPreview(event) {
    event.preventDefault()

    const menuName = this.element.querySelector('[name="custom_menu[name]"]')?.value || 'Menú Personalizado'
    const patientSelect = this.element.querySelector('[name="custom_menu[patient_id]"]')
    const patientId = patientSelect?.value
    let patientName = ''

    if (patientId) {
      const patients = this.patientsValue
      const patient = patients.find(p => p.id == patientId)
      patientName = patient?.name || ''
    }

    const today = new Date()
    const dateStr = today.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })

    // Obtener nuevos campos
    const patientWeight = this.element.querySelector('[name="custom_menu[patient_weight]"]')?.value || ''
    const startDate = this.element.querySelector('[name="custom_menu[start_date]"]')?.value || ''
    const endDate = this.element.querySelector('[name="custom_menu[end_date]"]')?.value || ''

    // Buscar observaciones por múltiples selectores
    const observationsField = this.element.querySelector('#custom_menu_observations') ||
                              this.element.querySelector('textarea[name="custom_menu[observations]"]') ||
                              this.element.querySelector('[data-target="custom-menu-editor.observations"]') ||
                              this.element.querySelector('[name="custom_menu[observations]"]')
    const observations = observationsField?.value?.trim() || ''

    // Obtener horarios de colaciones
    const mealtimeHours = this.getMealtimeHoursFromForm()

    // Obtener datos del menú
    const menuData = this.getMenuDataFromForm()

    // Generar contenido del preview
    const previewContent = document.getElementById('menuPreviewContent')
    previewContent.innerHTML = this.generatePreviewHtml(menuName, patientName, dateStr, menuData, {
      patientWeight,
      startDate,
      endDate,
      observations,
      mealtimeHours
    })

    // Mostrar modal
    const modal = document.getElementById('menuPreviewModal')
    $(modal).modal('show')
  }

  getMealtimeHoursFromForm() {
    const hours = {}
    const inputs = this.element.querySelectorAll('.mealtime-hour-input')
    inputs.forEach(input => {
      const mealtime = input.dataset.mealtime
      hours[mealtime] = input.value
    })
    return hours
  }

  formatDateRange(startDate, endDate) {
    if (!startDate || !endDate) return ''

    const formatDate = (dateStr) => {
      const date = new Date(dateStr + 'T00:00:00')
      return date.toLocaleDateString('es-AR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    }

    return `${formatDate(startDate)} al ${formatDate(endDate)}`
  }

  getMenuDataFromForm() {
    const menuData = {}
    const textareas = this.element.querySelectorAll('.menu-cell-textarea')

    textareas.forEach(textarea => {
      const day = textarea.dataset.day
      const mealtime = textarea.dataset.mealtime

      if (!menuData[day]) menuData[day] = {}
      menuData[day][mealtime] = textarea.value
    })

    return menuData
  }

  generatePreviewHtml(menuName, patientName, dateStr, menuData, extraData = {}) {
    const days = this.daysValue.length ? this.daysValue : ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo']
    const dayLabels = Object.keys(this.dayLabelsValue).length ? this.dayLabelsValue : {
      'lunes': 'Lunes', 'martes': 'Martes', 'miercoles': 'Miércoles',
      'jueves': 'Jueves', 'viernes': 'Viernes', 'sabado': 'Sábado', 'domingo': 'Domingo'
    }
    const mealtimes = this.mealtimesValue.length ? this.mealtimesValue : ['desayuno', 'media_manana', 'almuerzo', 'merienda', 'media_tarde', 'cena']
    const mealtimeLabels = Object.keys(this.mealtimeLabelsValue).length ? this.mealtimeLabelsValue : {
      'desayuno': 'Desayuno', 'media_manana': 'Media Mañana', 'almuerzo': 'Almuerzo',
      'merienda': 'Merienda', 'media_tarde': 'Media Tarde', 'cena': 'Cena'
    }
    const mealtimeColors = Object.keys(this.mealtimeColorsValue).length ? this.mealtimeColorsValue : {
      'desayuno': '#fff3cd', 'media_manana': '#d1ecf1', 'almuerzo': '#d4edda',
      'merienda': '#f8d7da', 'media_tarde': '#e2e3e5', 'cena': '#cce5ff'
    }
    const nutritionist = this.nutritionistValue || 'Nutricionista'
    const logoUrl = this.logoUrlValue

    // Datos extra
    const { patientWeight, startDate, endDate, observations, mealtimeHours } = extraData
    const dateRangeStr = this.formatDateRange(startDate, endDate)

    // Generar filas de la tabla con altura expandida
    let tableRows = ''
    mealtimes.forEach(mealtime => {
      const hour = mealtimeHours?.[mealtime] || ''
      let cells = `<td style="background-color: ${mealtimeColors[mealtime]}; font-weight: 600; text-align: center; padding: 20px 8px; font-size: 0.85rem; border: 1px solid #ddd; width: 100px; min-height: 85px;">
        <div>${mealtimeLabels[mealtime]}</div>
        ${hour ? `<div style="font-size: 0.75rem; font-weight: 400; color: #666; margin-top: 3px;">${hour} hs</div>` : ''}
      </td>`

      days.forEach(day => {
        const content = menuData[day]?.[mealtime] || ''
        const formattedContent = this.formatContentForPreview(content)
        cells += `<td style="padding: 15px 10px; vertical-align: top; font-size: 0.8rem; line-height: 1.5; background-color: ${mealtimeColors[mealtime]}20; border: 1px solid #ddd; min-height: 85px;">${formattedContent || '<span style="color: #bbb;">-</span>'}</td>`
      })

      tableRows += `<tr>${cells}</tr>`
    })

    // Generar headers de días
    let dayHeaders = '<th style="width: 100px; background: #33A036; color: white; padding: 12px 8px; font-size: 0.8rem; border: 1px solid #2d9134;"><i class="fas fa-utensils"></i></th>'
    days.forEach(day => {
      dayHeaders += `<th style="text-align: center; padding: 12px 8px; font-weight: 600; color: white; font-size: 0.8rem; background: #33A036; border: 1px solid #2d9134;">${dayLabels[day]}</th>`
    })

    return `
      <div style="background: white; min-height: 100%; font-family: 'Segoe UI', Arial, sans-serif;">
        <!-- Header con logo y datos -->
        <div style="background: #33A036; padding: 20px 30px; color: white;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div style="display: flex; align-items: center; gap: 25px;">
              ${logoUrl ? `<img src="${logoUrl}" alt="Cooking Service" style="height: 50px; filter: brightness(0) invert(1);">` : ''}
              ${patientName ? `
                <div>
                  <div style="font-size: 1.2rem; font-weight: 600;">
                    <i class="fas fa-user-circle" style="margin-right: 8px;"></i>${patientName}
                  </div>
                  ${dateRangeStr || patientWeight ? `
                    <div style="font-size: 0.9rem; opacity: 0.9; margin-top: 3px;">
                      ${dateRangeStr ? `<i class="fas fa-calendar-alt" style="margin-right: 6px;"></i>${dateRangeStr}` : ''}${dateRangeStr && patientWeight ? ' | ' : ''}${patientWeight ? `Peso Objetivo: ${patientWeight} kg` : ''}
                    </div>
                  ` : ''}
                </div>
              ` : ''}
            </div>
            <div style="text-align: right;">
              <div style="font-size: 0.9rem;">
                <i class="fas fa-user-md" style="margin-right: 6px;"></i>${nutritionist}
              </div>
              <div style="font-size: 0.85rem; opacity: 0.9; margin-top: 3px;">
                <i class="fas fa-calendar-alt" style="margin-right: 6px;"></i>${dateStr}
              </div>
            </div>
          </div>
        </div>

        <!-- Tabla del menú -->
        <div style="padding: 20px 30px;">
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr>${dayHeaders}</tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </div>

        <!-- Observaciones -->
        ${observations ? `
        <div style="padding: 0 30px 15px;">
          <div style="padding: 12px 15px; border-left: 4px solid #33A036; background: #f9f9f9;">
            <div style="color: #33A036; font-size: 0.9rem; font-weight: 600; margin-bottom: 5px;">
              <i class="fas fa-info-circle" style="margin-right: 6px;"></i>Observaciones
            </div>
            <div style="color: #555; font-size: 0.85rem; line-height: 1.5; white-space: pre-wrap;">${this.escapeHtml(observations)}</div>
          </div>
        </div>
        ` : ''}

        <!-- Footer con mensaje de cierre -->
        <div style="padding: 15px 30px; border-top: 1px solid #eee; text-align: center;">
          <p style="margin: 0 0 10px 0; color: #555; font-size: 0.9rem; font-style: italic;">
            Una buena alimentación y una adecuada actividad física te permiten descansar mejor.
          </p>
          ${logoUrl ? `<img src="${logoUrl}" alt="Cooking Service" style="height: 25px;">` : ''}
        </div>
      </div>
    `
  }

  formatContentForPreview(text) {
    if (!text) return ''

    let html = this.escapeHtml(text)

    // Negrita
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')

    // Cursiva
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
    html = html.replace(/_(.+?)_/g, '<em>$1</em>')

    // Viñetas
    html = html.replace(/^[•\-]\s+(.+)$/gm, '<div style="padding-left: 10px;">• $1</div>')

    // Listas numeradas
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<div style="padding-left: 10px;">$1. $2</div>')

    // Saltos de línea - usar <div> en vez de <br> para mejor renderizado en html2canvas/PDF
    html = html.replace(/\n/g, '</div><div style="margin: 0;">')
    html = `<div style="margin: 0;">${html}</div>`

    return html
  }

  toggleTemplate(event) {
    const patientSelect = this.element.querySelector('[name="custom_menu[patient_id]"]')
    const templateCheckbox = this.element.querySelector('#is_template_checkbox')

    if (event.target === templateCheckbox || event.target.id === 'is_template_checkbox') {
      if (templateCheckbox.checked && patientSelect) {
        patientSelect.value = ''
      }
    } else if (event.target === patientSelect || event.target.name === 'custom_menu[patient_id]') {
      if (patientSelect.value && templateCheckbox) {
        templateCheckbox.checked = false
      }
    }
  }

  loadTemplate(event) {
    const select = event.target
    const selectedOption = select.options[select.selectedIndex]

    if (!selectedOption.value) return

    const menuData = JSON.parse(selectedOption.dataset.menu || '{}')

    if (Object.keys(menuData).length === 0) {
      alert('Esta plantilla no tiene contenido')
      return
    }

    if (!confirm('¿Cargar el contenido de esta plantilla? Se sobrescribirá el contenido actual.')) {
      select.value = ''
      return
    }

    const textareas = this.element.querySelectorAll('.menu-cell-textarea')
    textareas.forEach(textarea => {
      const day = textarea.dataset.day
      const mealtime = textarea.dataset.mealtime

      if (menuData[day] && menuData[day][mealtime]) {
        textarea.value = menuData[day][mealtime]
        textarea.classList.add('border-success')
        setTimeout(() => textarea.classList.remove('border-success'), 500)
      }
    })

    select.value = ''
    this.showNotification('Plantilla cargada correctamente', 'success')
  }

  openEditorFromButton(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const day = button.dataset.day
    const mealtime = button.dataset.mealtime

    const textarea = this.element.querySelector(
      `textarea[data-day="${day}"][data-mealtime="${mealtime}"]`
    )

    if (textarea) {
      this.openEditorForTextarea(textarea, day, mealtime)
    }
  }

  openEditor(event) {
    event.preventDefault()
    const textarea = event.currentTarget
    const day = textarea.dataset.day
    const mealtime = textarea.dataset.mealtime
    this.openEditorForTextarea(textarea, day, mealtime)
  }

  openEditorForTextarea(textarea, day, mealtime) {
    this.currentTextarea = textarea

    const dayLabels = this.dayLabelsValue || {
      'lunes': 'Lunes', 'martes': 'Martes', 'miercoles': 'Miércoles',
      'jueves': 'Jueves', 'viernes': 'Viernes', 'sabado': 'Sábado', 'domingo': 'Domingo'
    }
    const mealtimeLabels = this.mealtimeLabelsValue || {
      'desayuno': 'Desayuno', 'media_manana': 'Media Mañana', 'almuerzo': 'Almuerzo',
      'merienda': 'Merienda', 'media_tarde': 'Media Tarde', 'cena': 'Cena'
    }

    const modalTitle = document.querySelector('[data-custom-menu-editor-target="modalTitle"]')
    if (modalTitle) {
      modalTitle.textContent = `${mealtimeLabels[mealtime] || mealtime} - ${dayLabels[day] || day}`
    }

    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    if (modalTextarea) {
      modalTextarea.value = textarea.value
      this.updatePreview()
    }

    this.updateModalClipboardIndicator()

    const modal = document.getElementById('menuCellEditorModal')
    if (modal) {
      $(modal).modal('show')
      $(modal).on('shown.bs.modal', () => {
        modalTextarea.focus()
        modalTextarea.setSelectionRange(modalTextarea.value.length, modalTextarea.value.length)
      })
    }
  }

  saveAndClose() {
    const modalTextarea = document.getElementById('menuCellEditorModal')?.querySelector('textarea')

    if (this.currentTextarea && modalTextarea) {
      const newValue = modalTextarea.value
      this.currentTextarea.value = newValue
      this.currentTextarea.dispatchEvent(new Event('input', { bubbles: true }))
      this.currentTextarea.dispatchEvent(new Event('change', { bubbles: true }))

      // Visual feedback
      const textarea = this.currentTextarea
      textarea.classList.add('border-success')
      textarea.style.borderWidth = '2px'
      setTimeout(() => {
        textarea.classList.remove('border-success')
        textarea.style.borderWidth = ''
      }, 1000)
    }

    const modal = document.getElementById('menuCellEditorModal')
    if (modal && typeof $ !== 'undefined') {
      $(modal).modal('hide')
    }

    this.currentTextarea = null

    // Auto-save after applying changes
    if (this.autoSaveUrlValue) {
      this.performAutoSave()
    }
  }

  formatBold() {
    this.wrapSelection('**', '**')
  }

  formatItalic() {
    this.wrapSelection('*', '*')
  }

  formatBullet() {
    this.insertAtLineStart('• ')
  }

  formatNumber() {
    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    if (!modalTextarea) return

    const start = modalTextarea.selectionStart
    const text = modalTextarea.value
    const lines = text.substring(0, start).split('\n')
    let lastNumber = 0

    for (let i = lines.length - 1; i >= 0; i--) {
      const match = lines[i].match(/^(\d+)\.\s/)
      if (match) {
        lastNumber = parseInt(match[1])
        break
      }
    }

    this.insertAtLineStart(`${lastNumber + 1}. `)
  }

  insertArrow() {
    this.insertText(' → ')
  }

  insertCheck() {
    this.insertText('✓ ')
  }

  wrapSelection(before, after) {
    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    if (!modalTextarea) return

    const start = modalTextarea.selectionStart
    const end = modalTextarea.selectionEnd
    const text = modalTextarea.value
    const selectedText = text.substring(start, end)

    modalTextarea.value = text.substring(0, start) + before + selectedText + after + text.substring(end)
    modalTextarea.setSelectionRange(start + before.length, end + before.length)
    modalTextarea.focus()
    this.updatePreview()
  }

  insertText(text) {
    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    if (!modalTextarea) return

    const start = modalTextarea.selectionStart
    const currentText = modalTextarea.value

    modalTextarea.value = currentText.substring(0, start) + text + currentText.substring(start)
    modalTextarea.setSelectionRange(start + text.length, start + text.length)
    modalTextarea.focus()
    this.updatePreview()
  }

  insertAtLineStart(prefix) {
    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    if (!modalTextarea) return

    const start = modalTextarea.selectionStart
    const text = modalTextarea.value
    let lineStart = text.lastIndexOf('\n', start - 1) + 1

    modalTextarea.value = text.substring(0, lineStart) + prefix + text.substring(lineStart)
    modalTextarea.setSelectionRange(start + prefix.length, start + prefix.length)
    modalTextarea.focus()
    this.updatePreview()
  }

  updatePreview() {
    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    const preview = document.querySelector('[data-custom-menu-editor-target="preview"]')
    const charCount = document.querySelector('[data-custom-menu-editor-target="charCount"]')

    if (!modalTextarea || !preview) return

    const text = modalTextarea.value

    if (charCount) {
      charCount.textContent = `${text.length} caracteres`
    }

    let html = this.escapeHtml(text)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
    html = html.replace(/_(.+?)_/g, '<em>$1</em>')
    html = html.replace(/^[•\-]\s+(.+)$/gm, '<li style="margin-left: 1rem;">$1</li>')
    html = html.replace(/^\d+\.\s+(.+)$/gm, '<li style="margin-left: 1rem; list-style-type: decimal;">$1</li>')
    html = html.replace(/\n/g, '<br>')

    preview.innerHTML = html || '<span class="text-muted">La vista previa aparecerá aquí...</span>'
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  autoSave(event) {
    const textarea = event.target
    textarea.style.borderColor = '#33A036'

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    this.saveTimeout = setTimeout(() => {
      textarea.style.borderColor = ''
    }, 800)
  }

  async performAutoSave() {
    if (!this.autoSaveUrlValue || this.isAutoSaving) return

    this.isAutoSaving = true
    const indicator = document.getElementById('autoSaveIndicator')

    if (indicator) {
      indicator.style.display = 'inline-block'
      indicator.innerHTML = '<i class="fas fa-spinner fa-spin mr-1"></i>Guardando...'
      indicator.className = 'badge badge-warning'
    }

    try {
      const formData = this.collectFormData()
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.autoSaveUrlValue, {
        method: this.menuIdValue ? 'PATCH' : 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: formData
      })

      const data = await response.json()

      if (data.success) {
        if (indicator) {
          indicator.innerHTML = `<i class="fas fa-check mr-1"></i>Guardado ${data.updated_at}`
          indicator.className = 'badge badge-success'
        }

        // If it was a new menu, update references to point to the persisted record
        if (!this.menuIdValue && data.id) {
          this.menuIdValue = data.id
          this.autoSaveUrlValue = `/custom_menus/${data.id}/auto_save`

          // Update browser URL
          history.replaceState(null, '', `/custom_menus/${data.id}/edit`)

          // Update form action to point to update
          const form = this.element.closest('form') || this.element
          if (form.tagName === 'FORM') {
            form.action = `/custom_menus/${data.id}`
            // Add hidden _method field for PATCH if not present
            if (!form.querySelector('input[name="_method"]')) {
              const methodInput = document.createElement('input')
              methodInput.type = 'hidden'
              methodInput.name = '_method'
              methodInput.value = 'patch'
              form.appendChild(methodInput)
            }
          }
        }
      } else {
        if (indicator) {
          const errorMsg = data.errors?.join(', ') || 'Error al guardar'
          indicator.innerHTML = `<i class="fas fa-exclamation-triangle mr-1"></i>${errorMsg}`
          indicator.className = 'badge badge-danger'
        }
      }
    } catch (error) {
      console.error('Auto-save error:', error)
      if (indicator) {
        indicator.innerHTML = '<i class="fas fa-exclamation-triangle mr-1"></i>Error de conexión'
        indicator.className = 'badge badge-danger'
      }
    } finally {
      this.isAutoSaving = false
      // Reset the 2-minute timer for next auto-save
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer)
      }
      this.autoSaveTimer = setTimeout(() => this.performAutoSave(), 120000)
    }
  }

  collectFormData() {
    const form = this.element.closest('form') || this.element
    return new FormData(form)
  }

  highlightRow(event) {
    const row = event.target.closest('tr')
    if (row) row.classList.add('editing')
  }

  unhighlightRow(event) {
    const row = event.target.closest('tr')
    if (row) row.classList.remove('editing')
  }

  // --- Copy / Paste between cells ---

  copyCell(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const day = button.dataset.day
    const mealtime = button.dataset.mealtime

    const textarea = this.element.querySelector(
      `textarea[data-day="${day}"][data-mealtime="${mealtime}"]`
    )
    if (!textarea || !textarea.value.trim()) return

    this.clipboardContent = textarea.value

    const dayLabels = this.dayLabelsValue || {}
    const mealtimeLabels = this.mealtimeLabelsValue || {}
    this.clipboardLabel = `${mealtimeLabels[mealtime] || mealtime} - ${dayLabels[day] || day}`

    // Update all paste buttons visibility
    this.updatePasteButtons()

    this.showNotification(`Copiado: ${this.clipboardLabel}`, 'success')
  }

  pasteCell(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.clipboardContent) return

    const button = event.currentTarget
    const day = button.dataset.day
    const mealtime = button.dataset.mealtime

    const textarea = this.element.querySelector(
      `textarea[data-day="${day}"][data-mealtime="${mealtime}"]`
    )
    if (!textarea) return

    textarea.value = this.clipboardContent
    textarea.dispatchEvent(new Event('input', { bubbles: true }))
    textarea.dispatchEvent(new Event('change', { bubbles: true }))

    // Visual feedback
    textarea.classList.add('border-success')
    textarea.style.borderWidth = '2px'
    setTimeout(() => {
      textarea.classList.remove('border-success')
      textarea.style.borderWidth = ''
    }, 1000)

    // Auto-save
    if (this.autoSaveUrlValue) {
      this.performAutoSave()
    }
  }

  copyFromModal() {
    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    if (!modalTextarea || !modalTextarea.value.trim()) return

    this.clipboardContent = modalTextarea.value

    const modalTitle = document.querySelector('[data-custom-menu-editor-target="modalTitle"]')
    this.clipboardLabel = modalTitle?.textContent || 'celda'

    this.updatePasteButtons()
    this.updateModalClipboardIndicator()

    this.showNotification(`Copiado: ${this.clipboardLabel}`, 'success')
  }

  pasteToModal() {
    if (!this.clipboardContent) return

    const modalTextarea = document.querySelector('[data-custom-menu-editor-target="modalTextarea"]')
    if (!modalTextarea) return

    modalTextarea.value = this.clipboardContent
    modalTextarea.focus()
    this.updatePreview()
  }

  updatePasteButtons() {
    const hasCopy = !!this.clipboardContent
    this.element.querySelectorAll('.paste-btn').forEach(btn => {
      btn.style.display = hasCopy ? '' : 'none'
    })
  }

  updateModalClipboardIndicator() {
    const indicator = document.getElementById('modalClipboardIndicator')
    if (indicator) {
      if (this.clipboardContent) {
        indicator.style.display = ''
        indicator.title = `Portapapeles: ${this.clipboardLabel}`
        indicator.querySelector('.clipboard-label').textContent = this.clipboardLabel
      } else {
        indicator.style.display = 'none'
      }
    }
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div')
    notification.className = `alert alert-${type} alert-dismissible fade show position-fixed`
    notification.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 250px;'
    notification.innerHTML = `
      ${message}
      <button type="button" class="close" data-dismiss="alert">
        <span>&times;</span>
      </button>
    `
    document.body.appendChild(notification)
    setTimeout(() => notification.remove(), 3000)
  }
}
